import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { http, API } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Video, Globe, Mail, Phone, Calendar, CheckCircle2, Loader2, Facebook, Linkedin, MessageSquare, Link2, Stethoscope } from 'lucide-react';

const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');

function setMeta(sel, val) {
  const el = document.head.querySelector(sel);
  if (!el) return;
  if (!el.hasAttribute('data-seo-orig')) el.setAttribute('data-seo-orig', el.getAttribute('content') ?? el.getAttribute('href') ?? '');
  if (el.tagName === 'LINK') el.setAttribute('href', val); else el.setAttribute('content', val);
}
function restoreMeta() {
  document.head.querySelectorAll('[data-seo-orig]').forEach((el) => {
    const o = el.getAttribute('data-seo-orig');
    if (el.tagName === 'LINK') el.setAttribute('href', o); else el.setAttribute('content', o);
    el.removeAttribute('data-seo-orig');
  });
}

export default function PublicTherapistProfile() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | notfound

  const load = useCallback(async () => {
    try {
      const r = await http.get(`/therapists/${slug}`);
      setT(r.data); setState('ok');
    } catch { setState('notfound'); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (state !== 'ok' || !t) return;
    const url = `${PUBLIC_BASE}/therapists/${t.slug || slug}`;
    const title = `${t.name}${t.credentials ? ', ' + t.credentials : ''} — Brotherhood+ Therapist`;
    const desc = (t.bio || `Connect with ${t.name}, a culturally responsive therapist in the Brotherhood+ directory.`).slice(0, 200);
    const img = t.photo ? (t.photo.startsWith('http') ? t.photo : `${PUBLIC_BASE}${t.photo}`) : `${PUBLIC_BASE}/og-directory.png`;
    document.title = title;
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:image"]', img);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', desc);
    setMeta('meta[name="twitter:image"]', img);
    setMeta('link[rel="canonical"]', url);
    return () => { document.title = 'Brotherhood+'; restoreMeta(); };
  }, [state, t, slug]);

  const shareUrl = `${PUBLIC_BASE}/therapists/${slug}`;
  // Scrapers (FB/LinkedIn) hit the backend OG endpoint which serves a perfect
  // preview card and 302-redirects real visitors to the pretty profile URL.
  const scrapeUrl = `${API}/therapists/${slug}/share`;
  const shareText = t ? `Meet ${t.name} on the Brotherhood+ therapist directory` : 'Brotherhood+ therapist';
  const share = (net) => {
    const u = encodeURIComponent(scrapeUrl); const txt = encodeURIComponent(shareText);
    if (net === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`, '_blank', 'noopener,width=640,height=580');
    else if (net === 'linkedin') window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`, '_blank', 'noopener,width=640,height=580');
    else if (net === 'sms') window.location.href = `sms:?&body=${txt}%20${encodeURIComponent(shareUrl)}`;
    else navigator.clipboard.writeText(shareUrl).then(() => toast.success('Link copied')).catch(() => toast.error('Could not copy'));
  };

  if (state === 'loading') return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center"><Loader2 className="animate-spin text-navy/40" size={24}/></div>;

  if (state === 'notfound') return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4" data-testid="therapist-profile-notfound">
      <div className="bg-white rounded-3xl p-10 text-center card-soft max-w-md">
        <p className="font-heading text-2xl">Profile not found</p>
        <p className="text-navy/60 mt-2">This therapist may no longer be listed.</p>
        <button onClick={() => nav('/directory')} className="mt-5 bg-navy text-white rounded-full px-6 py-3 text-sm font-medium">Browse the directory</button>
      </div>
    </div>
  );

  const modality = (t.modality || (t.telehealth ? 'telehealth' : '')).toLowerCase();
  const states = t.states || (t.state ? [t.state] : []);
  const booking = t.booking_url || t.website;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="therapist-profile-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-navy/10">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => nav('/directory')} className="inline-flex items-center gap-2 text-sm text-navy/70 hover:text-navy" data-testid="profile-back"><ArrowLeft size={15}/> Directory</button>
          <button onClick={() => nav('/login')} className="bg-navy text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-navy-soft">Join Brotherhood+</button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8 pb-24 space-y-5">
        <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            {t.photo ? (
              <img src={t.photo} alt={`Headshot of ${t.name}`} className="w-28 h-28 rounded-full object-cover ring-2 ring-gold/40 shadow-sm shrink-0"/>
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-beige to-gold/45 text-navy flex items-center justify-center font-heading text-3xl ring-2 ring-gold/30 shrink-0">
                {(t.name || 'T').split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-3xl leading-tight">{t.name}</h1>
              {t.credentials && <p className="text-navy/55 mt-1">{t.credentials}</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(modality === 'both' || modality === 'telehealth') && <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide bg-gold/15 text-navy rounded-full px-2.5 py-1"><Video size={11}/> Telehealth</span>}
                {(modality === 'both' || modality === 'in-person') && <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide bg-beige text-navy/70 rounded-full px-2.5 py-1"><MapPin size={11}/> In-person</span>}
                {t.faith_informed && <span className="text-[11px] uppercase tracking-wide bg-beige text-navy/70 rounded-full px-2.5 py-1">Faith-informed</span>}
                {t.trauma_informed && <span className="text-[11px] uppercase tracking-wide bg-beige text-navy/70 rounded-full px-2.5 py-1">Trauma-informed</span>}
              </div>
            </div>
          </div>

          {t.bio && <p className="text-navy/75 mt-5 leading-relaxed whitespace-pre-line">{t.bio}</p>}

          {!!(t.specialties || []).length && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold mb-2">Specialties</p>
              <div className="flex flex-wrap gap-1.5">{t.specialties.map((s) => <span key={s} className="text-xs bg-navy/5 text-navy/70 rounded-full px-3 py-1">{s}</span>)}</div>
            </div>
          )}

          <div className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-navy/65">
            {!!states.length && <p className="flex items-center gap-1.5"><MapPin size={13}/> Licensed: {states.join(', ')}</p>}
            {!!(t.languages || []).length && <p className="flex items-center gap-1.5"><Globe size={13}/> {t.languages.join(', ')}</p>}
            {!!(t.insurance || []).length && <p className="flex items-center gap-1.5"><CheckCircle2 size={13}/> {t.insurance.join(', ')}</p>}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {booking && <a href={booking} target="_blank" rel="noreferrer" data-testid="profile-book" className="inline-flex items-center gap-1.5 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-soft"><Calendar size={14}/> Book / Website</a>}
            {t.email && <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1.5 bg-beige text-navy rounded-full px-5 py-2.5 text-sm font-medium hover:brightness-95"><Mail size={14}/> Email</a>}
            {t.phone && <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1.5 bg-beige text-navy rounded-full px-5 py-2.5 text-sm font-medium hover:brightness-95"><Phone size={14}/> Call</a>}
          </div>
        </div>

        {/* Share */}
        <div className="bg-white rounded-2xl p-5 card-soft" data-testid="profile-share">
          <p className="text-sm font-semibold mb-3">Share this profile</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => share('facebook')} data-testid="share-facebook" className="inline-flex items-center gap-1.5 bg-[#1877F2] text-white rounded-full px-4 py-2 text-xs font-medium hover:opacity-90"><Facebook size={14}/> Facebook</button>
            <button onClick={() => share('linkedin')} data-testid="share-linkedin" className="inline-flex items-center gap-1.5 bg-[#0A66C2] text-white rounded-full px-4 py-2 text-xs font-medium hover:opacity-90"><Linkedin size={14}/> LinkedIn</button>
            <button onClick={() => share('sms')} data-testid="share-sms" className="inline-flex items-center gap-1.5 bg-beige text-navy rounded-full px-4 py-2 text-xs font-medium hover:brightness-95"><MessageSquare size={14}/> Text</button>
            <button onClick={() => share('copy')} data-testid="share-copy" className="inline-flex items-center gap-1.5 bg-navy text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-navy-soft"><Link2 size={14}/> Copy link</button>
          </div>
        </div>

        <button onClick={() => nav('/therapist-apply')} className="w-full text-center text-sm text-navy/60 hover:text-navy inline-flex items-center justify-center gap-2" data-testid="profile-apply">
          <Stethoscope size={14}/> Are you a therapist? Apply to be listed →
        </button>
        <p className="text-xs text-navy/45 text-center">Brotherhood+ lists independent providers and is not a referral service. Verify credentials with your state board before booking.</p>
      </div>
    </div>
  );
}
